/**
 * Learner-facing copy for every playbook in `src/platform/playbooks/`.
 * Playbooks only hold keys; a test asserts every key exists in all four
 * locales, so a new playbook cannot ship half-translated.
 */
const en = {
  "crisis.name": "Crisis negotiation",
  "crisis.summary":
    "For crisis and hostage negotiators, and the students training to join a team. Prepare from intel, rehearse the subject, write the after-action report.",
  "crisis.counterpart": "Subject",
  "crisis.learner": "Negotiator",
  "crisis.prep.situation": "Situation",
  "crisis.prep.situationHint":
    "What is happening, where, and since when. One or two paragraphs is enough.",
  "crisis.prep.subject": "About the subject",
  "crisis.prep.subjectHint":
    "Name or alias, age, state of mind, history (mental health, substance use, weapons access), and what they have said so far.",
  "crisis.prep.people": "People around them",
  "crisis.prep.peopleHint":
    "Family, friends, hostages or witnesses: who they are and what they told you.",
  "crisis.prep.demands": "Demands and deadlines",
  "crisis.prep.demandsHint":
    "What the subject is asking for, by when, and any threats attached.",
  "crisis.prep.hooksAndTriggers": "Hooks and triggers",
  "crisis.prep.hooksAndTriggersHint":
    "What matters to them (hooks) and what sets them off (triggers).",
  "crisis.prep.pastContact": "Past contact",
  "crisis.prep.pastContactHint":
    "Paste a transcript or notes from an earlier call with this subject, if there was one.",
};

export type PlaybookCopyTable = typeof en;

export const PLAYBOOK_COPY: Record<"en" | "uk" | "pl" | "ro", PlaybookCopyTable> =
  {
    en,
    uk: {
      "crisis.name": "Кризові переговори",
      "crisis.summary":
        "Для кризових переговорників і студентів, які готуються до роботи в команді. Підготовка за розвідданими, репетиція з суб’єктом, звіт після операції.",
      "crisis.counterpart": "Суб’єкт",
      "crisis.learner": "Переговорник",
      "crisis.prep.situation": "Ситуація",
      "crisis.prep.situationHint":
        "Що відбувається, де і відколи. Достатньо одного-двох абзаців.",
      "crisis.prep.subject": "Про суб’єкта",
      "crisis.prep.subjectHint":
        "Ім’я чи прізвисько, вік, стан, історія (психічне здоров’я, вживання речовин, доступ до зброї) і що він уже сказав.",
      "crisis.prep.people": "Люди навколо",
      "crisis.prep.peopleHint":
        "Родина, друзі, заручники чи свідки: хто вони і що розповіли.",
      "crisis.prep.demands": "Вимоги й дедлайни",
      "crisis.prep.demandsHint":
        "Чого вимагає суб’єкт, до коли і які погрози з цим пов’язані.",
      "crisis.prep.hooksAndTriggers": "Гачки й тригери",
      "crisis.prep.hooksAndTriggersHint":
        "Що для нього важливо (гачки) і що виводить його з рівноваги (тригери).",
      "crisis.prep.pastContact": "Попередній контакт",
      "crisis.prep.pastContactHint":
        "Вставте транскрипт або нотатки з попередньої розмови з цим суб’єктом, якщо вона була.",
    },
    pl: {
      "crisis.name": "Negocjacje kryzysowe",
      "crisis.summary":
        "Dla negocjatorów kryzysowych i studentów przygotowujących się do pracy w zespole. Przygotowanie na podstawie informacji, próba z osobą, raport po akcji.",
      "crisis.counterpart": "Osoba",
      "crisis.learner": "Negocjator",
      "crisis.prep.situation": "Sytuacja",
      "crisis.prep.situationHint":
        "Co się dzieje, gdzie i od kiedy. Wystarczy jeden lub dwa akapity.",
      "crisis.prep.subject": "O tej osobie",
      "crisis.prep.subjectHint":
        "Imię lub pseudonim, wiek, stan, historia (zdrowie psychiczne, substancje, dostęp do broni) i co już powiedziała.",
      "crisis.prep.people": "Osoby wokół",
      "crisis.prep.peopleHint":
        "Rodzina, przyjaciele, zakładnicy lub świadkowie: kim są i co powiedzieli.",
      "crisis.prep.demands": "Żądania i terminy",
      "crisis.prep.demandsHint":
        "Czego żąda ta osoba, do kiedy i jakie groźby się z tym wiążą.",
      "crisis.prep.hooksAndTriggers": "Punkty zaczepienia i wyzwalacze",
      "crisis.prep.hooksAndTriggersHint":
        "Co jest dla niej ważne i co wyprowadza ją z równowagi.",
      "crisis.prep.pastContact": "Wcześniejszy kontakt",
      "crisis.prep.pastContactHint":
        "Wklej transkrypcję lub notatki z wcześniejszej rozmowy z tą osobą, jeśli była.",
    },
    ro: {
      "crisis.name": "Negociere în criză",
      "crisis.summary":
        "Pentru negociatorii de criză și studenții care se pregătesc să intre într-o echipă. Pregătire din informații, repetiție cu subiectul, raport după acțiune.",
      "crisis.counterpart": "Subiect",
      "crisis.learner": "Negociator",
      "crisis.prep.situation": "Situația",
      "crisis.prep.situationHint":
        "Ce se întâmplă, unde și de când. Unul sau două paragrafe sunt suficiente.",
      "crisis.prep.subject": "Despre subiect",
      "crisis.prep.subjectHint":
        "Nume sau poreclă, vârstă, stare, istoric (sănătate mintală, substanțe, acces la arme) și ce a spus până acum.",
      "crisis.prep.people": "Persoanele din jur",
      "crisis.prep.peopleHint":
        "Familie, prieteni, ostatici sau martori: cine sunt și ce v-au spus.",
      "crisis.prep.demands": "Cereri și termene",
      "crisis.prep.demandsHint":
        "Ce cere subiectul, până când și ce amenințări le însoțesc.",
      "crisis.prep.hooksAndTriggers": "Puncte de sprijin și declanșatori",
      "crisis.prep.hooksAndTriggersHint":
        "Ce contează pentru el și ce îl scoate din echilibru.",
      "crisis.prep.pastContact": "Contact anterior",
      "crisis.prep.pastContactHint":
        "Lipiți o transcriere sau notițe dintr-o convorbire anterioară cu acest subiect, dacă a existat.",
    },
  };

/** Resolve a playbook copy key. Unknown keys fail loudly in development. */
export function playbookCopy(locale: string) {
  const table =
    PLAYBOOK_COPY[locale as keyof typeof PLAYBOOK_COPY] ?? PLAYBOOK_COPY.en;
  return (key: string): string => {
    const text = (table as Record<string, string>)[key];
    if (text !== undefined) return text;
    console.error(`[playbooks] missing copy key "${key}"`);
    return key;
  };
}
